import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, Heart, Truck, Award, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useCart } from '../../context/CartContext';

const ProductCard = ({ product, compact = false }) => {
  const { addItem } = useCart();

  const getBadgeConfig = (badge) => {
    switch (badge) {
      case 'therapist-recommended':
        return { label: 'Therapist Recommended', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'best-seller':
        return { label: 'Best Seller', className: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'verified':
        return { label: 'Verified', className: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'best-for-beginners':
        return { label: 'Best for Beginners', className: 'bg-purple-50 text-purple-700 border-purple-200' };
      case 'bundle-eligible':
        return { label: 'Bundle Eligible', className: 'bg-rose-50 text-rose-700 border-rose-200' };
      case 'fast-shipping':
        return { label: 'Fast Shipping', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' };
      default:
        return { label: badge, className: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <Card className="group overflow-hidden border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 bg-white">
      <Link to={`/product/${product.id}`}>
        <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
          <img
            src={product.images?.[0] || 'https://placehold.co/400x400/f3f4f6/9ca3af?text=Product'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {discount > 0 && (
            <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
              -{discount}%
            </div>
          )}
          {product.fastShipping && (
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm p-1.5 rounded-full shadow-sm">
              <Truck className="w-4 h-4 text-cyan-600" />
            </div>
          )}
          <button className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
            <Heart className="w-4 h-4 text-gray-400 hover:text-red-500 transition-colors" />
          </button>
        </div>
      </Link>

      <CardContent className="p-4">
        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {product.badges?.slice(0, 2).map((badge) => {
            const config = getBadgeConfig(badge);
            return (
              <span
                key={badge}
                className={`text-xs font-medium px-2 py-0.5 rounded-full border ${config.className}`}
              >
                {badge === 'therapist-recommended' && <Award className="w-3 h-3 inline mr-1" />}
                {config.label}
              </span>
            );
          })}
        </div>

        {/* Name & Description */}
        <Link to={`/product/${product.id}`}>
          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-red-600 transition-colors">
            {product.name}
          </h3>
        </Link>
        {!compact && (
          <p className="text-sm text-gray-500 mb-3 line-clamp-1">{product.shortDescription}</p>
        )}

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="flex items-center">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-sm font-medium text-gray-900 ml-1">{product.rating}</span>
          </div>
          <span className="text-sm text-gray-400">({product.reviewCount})</span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-gray-900">${product.price.toFixed(2)}</span>
            {product.originalPrice && (
              <span className="text-sm text-gray-400 line-through">
                ${product.originalPrice.toFixed(2)}
              </span>
            )}
          </div>
          <Button
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              addItem(product.id);
            }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full h-9 px-4 shadow-sm hover:shadow-md transition-all"
          >
            <ShoppingCart className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;